import { useState, useCallback, useEffect, useRef } from "react";
import {
  ServerSideConfig,
  ServerSideParams,
  ServerSideState,
  ServerSideResponse,
} from "../types";

export function useServerSideTable<T>(
  serverConfig: ServerSideConfig<T>,
  initialPageSize: number = 10
) {
  const [state, setState] = useState<ServerSideState>({
    currentPage: 1,
    pageSize: initialPageSize,
    filters: {},
    sorting: {
      field: null,
      direction: null,
    },
    dateRange: {
      field: null,
      range: "all",
      startDate: null,
      endDate: null,
    },
  });

  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const requestCounterRef = useRef(0);
  const lastRequestRef = useRef<string>("");
  const isUpdatingRef = useRef(false);

  const buildApiParams = useCallback(
    (currentState: ServerSideState): ServerSideParams => {
      const params: ServerSideParams = {
        page: currentState.currentPage,
        limit: currentState.pageSize,
        ...currentState.filters,
      };

      if (currentState.sorting.field && currentState.sorting.direction) {
        params.sortBy = currentState.sorting.field;
        params.sortOrder = currentState.sorting.direction;
      }

      if (
        currentState.dateRange.field &&
        currentState.dateRange.range !== "all"
      ) {
        params.dateField = currentState.dateRange.field;
        params.dateRange = currentState.dateRange.range;

        if (currentState.dateRange.startDate) {
          params.startDate = currentState.dateRange.startDate;
        }
        if (currentState.dateRange.endDate) {
          params.endDate = currentState.dateRange.endDate;
        }
      }

      return params;
    },
    []
  );

  // Fetch data from server
  const fetchData = useCallback(
    async (currentState: ServerSideState) => {
      if (!serverConfig.enabled || !serverConfig.onDataRequest) return;

      // new
      const params = buildApiParams(currentState);
      const requestKey = JSON.stringify(params);

      if (requestKey === lastRequestRef.current && loading) {
        return;
      }

      lastRequestRef.current = requestKey;
      requestCounterRef.current++;

      setLoading(true);
      setError(null);

      try {
        const params = buildApiParams(currentState);
        const response: ServerSideResponse<T> =
          await serverConfig.onDataRequest(params);

        if (response.success) {
          setData(response.data);
          setTotalCount(response.meta.total);
        } else {
          setError(response.message || "Failed to fetch data");
          setData([]);
          setTotalCount(0);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        setData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        setHasInitialLoad(true);
      }
    },
    [serverConfig, buildApiParams, loading]
  );

  // Update state and trigger fetch
  const updateState = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (updates: Partial<ServerSideState>, immediate = false) => {
      isUpdatingRef.current = true;

      setState((prevState) => {
        const newState = { ...prevState, ...updates };

        if (updates.filters || updates.sorting || updates.dateRange) {
          newState.currentPage = 1;
        }

        setTimeout(() => {
          fetchData(newState);
          isUpdatingRef.current = false;
        }, 0);
        return newState;
      });
    },
    [fetchData]
  );

  // Public API methods
  const setPage = useCallback(
    (page: number) => {
      updateState({ currentPage: page }, true);
    },
    [updateState]
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      updateState({ pageSize, currentPage: 1 }, true);
    },
    [updateState]
  );

  const setColumnFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (columnId: string, value: any) => {
      updateState({
        filters: {
          ...state.filters,
          [columnId]: value || undefined,
        },
      });
    },
    [updateState, state.filters]
  );

  const setSorting = useCallback(
    (field: string | null, direction: "asc" | "desc" | null) => {
      updateState(
        {
          sorting: { field, direction },
        },
        true
      );
    },
    [updateState]
  );

  const setDateRangeFilter = useCallback(
    (
      field: string | null,
      range: string,
      startDate?: string,
      endDate?: string
    ) => {
      updateState({
        dateRange: {
          field,
          range,
          startDate: startDate || null,
          endDate: endDate || null,
        },
      });
    },
    [updateState]
  );

  const clearAllFilters = useCallback(() => {
    updateState(
      {
        filters: {},
        sorting: { field: null, direction: null },
        dateRange: {
          field: null,
          range: "all",
          startDate: null,
          endDate: null,
        },
        currentPage: 1,
      },
      true
    );
  }, [updateState]);

  const refresh = useCallback(() => {
    fetchData(state);
  }, [fetchData, state]);

  // Initial fetch
  useEffect(() => {
    if (serverConfig.enabled && !hasInitialLoad) {
      fetchData(state);
    }
  }, [serverConfig.enabled, hasInitialLoad, fetchData, state]);

  return {
    // Data
    data,
    totalCount,
    loading,
    error,

    // State
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    filters: state.filters,
    sorting: state.sorting,
    dateRange: state.dateRange,

    // Actions
    setPage,
    setPageSize,
    setColumnFilter,
    setSorting,
    setDateRangeFilter,
    clearAllFilters,
    refresh,

    // Computed values
    totalPages: Math.ceil(totalCount / state.pageSize),
    hasNextPage: state.currentPage < Math.ceil(totalCount / state.pageSize),
    hasPreviousPage: state.currentPage > 1,
  };
}
